<template>
    <PreavisConsultModal
        ref="consultRef"
        :contentMaxHeight="'75vh'"
        :maxWidth="'80%'"
        :title="$t('modale.preavisIntervention.title')"
        :visible="true"
        :width="'75vw'"
        @cancel="cancel"
        @delete="onDelete"
        @edit="onEdit"
        @toggleActif="onToggleActif"
        @open-create="openCreate"
    />

    <PreavisCreateModal
        :initialStep="createDirectPayload ? createDirectPayload.initialStep : 1"
        :preselectedEquipement="createDirectPayload ? createDirectPayload.equipement : null"
        :preselectedSiteId="createDirectPayload && createDirectPayload.equipement ? createDirectPayload.equipement.adresseProjetId : null"
        :title="$t('modale.preavisIntervention.create.title')"
        :visible="showCreateModal"
        @cancel="closeCreateModal"
        @created="onCreateSuccess"
    />

    <PreavisConfirmationCreatedModal
        :hideNouveauPreavis="!!createDirectPayload"
        :visible="showCreatedModal"
        @close="closeAllPreavisModals"
        @mes-preavis="onMesPreavis"
        @nouveau-preavis="onNouveauPreavis"
    />

    <PreavisDeleteModal
        :message="$t('preavisIntervention.modale.delete.message', { objet: alertPendingDelete?.objet })"
        :title="$t('preavisIntervention.modale.delete.title')"
        :visible="showDeleteModal"
        @cancel="closeDeleteModal"
        @confirm="deleteDemande"
    />

    <Modal v-if="showForm" @cancel="closeForm">
        <ModalCardConfirm
            :activeValidate="editFormValid && !isSubmitting"
            :content-max-height="'75vh'"
            :headerTitle="$t('modale.preavisIntervention.edit.title')"
            :max-width="'80%'"
            :overflowContent="'visible'"
            :width="'40vw'"
            @cancel="closeForm"
            @confirm="onSubmit"
        >
            <PreavisFormCreateEdit
                ref="editFormulaireRef"
                :initialDelai="form.delai || 1"
                :initialEmails="form.emails"
                :initialObjet="form.objet"
                mode="edit"
                @state-change="onEditStateChange"
            />
        </ModalCardConfirm>
    </Modal>
    <WaitingModal v-if="isSubmitting" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Modal from '@/components/ui-components/Modal.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { store } from '@/plugins/store';
import { PREAVIS } from '@/modules/PreavisIntervention/store/mutation-types';
import { PreavisInterventionModuleAction } from '@/modules/PreavisIntervention/store/actions-types';
import { PreavisInterventionDTO } from '@/models/PreavisIntervention/PreavisInterventionDTO';
import {
    UpdateActivationPreavisInterventionDTO
} from '@/models/PreavisIntervention/UpdateActivationPreavisInterventionDTO';
import { UpdatePreavisInterventionDTO } from '@/models/PreavisIntervention/UpdatePreavisInterventionDTO';
import PreavisDeleteModal from '@/modules/PreavisIntervention/components/PreavisDeleteModal.vue';
import PreavisFormCreateEdit from '@/modules/PreavisIntervention/components/PreavisFormCreateEdit.vue';
import PreavisConsultModal from '@/modules/PreavisIntervention/components/PreavisConsultModal.vue';
import ModalCardConfirm from '@/components/ui-components/ModalCardConfirm.vue';
import PreavisConfirmationCreatedModal
    from '@/modules/PreavisIntervention/components/PreavisConfirmationCreatedModal.vue';
import PreavisCreateModal from '@/modules/PreavisIntervention/components/PreavisCreateModal.vue';
import { CreateDirectPayload } from '@/modules/PreavisIntervention/store/state';

export default defineComponent({
    name: 'PreavisInterventionModale',
    components: {
        PreavisCreateModal,
        PreavisConfirmationCreatedModal,
        PreavisFormCreateEdit,
        Modal,
        ModalCardConfirm,
        PreavisDeleteModal,
        PreavisConsultModal,
        WaitingModal,
    },
    computed: {
        createDirectPayload(): CreateDirectPayload | null {
            return store.state.preavisInterventionState.createDirect;
        },
        isSubmitting(): boolean {
            return store.state.preavisInterventionState.isSubmitting;
        },
    },
    data() {
        return {
            PreavisInterventionModuleAction,
            showForm: false as boolean,
            showCreateModal: false as boolean,
            showCreatedModal: false as boolean,
            editingId: null as number | null,
            showDeleteModal: false as boolean,
            alertPendingDelete: null as PreavisInterventionDTO | null,
            form: {
                objet: '',
                delai: undefined as number | undefined,
                actif: true,
                emails: [] as string[],
            },
            editFormValid: false as boolean,
            editStatePayload: null as any,
            isTogglingActif: false as boolean,
        };
    },
    mounted() {
        if (this.createDirectPayload) {
            this.showCreateModal = true;
        }
    },
    methods: {
        cancel() {
            store.commit(`preavisInterventionState/${PREAVIS.SET_CREATE_DIRECT}`, null);
            store.commit(`preavisInterventionState/${PREAVIS.SET_MODAL}`, false);
        },
        triggerSearch() {
            const child: any = (this.$refs as any)?.consultRef;
            if (child && typeof child.refreshList === 'function') child.refreshList();
        },
        async onToggleActif(dto: PreavisInterventionDTO) {
            if (!dto || this.isTogglingActif) return;
            this.isTogglingActif = true;
            const payload: UpdateActivationPreavisInterventionDTO = { actif: !dto.actif } as any;
            try {
                await store.dispatch(`preavisInterventionState/${PreavisInterventionModuleAction.UPDATE_ACTIVATION}`, {
                    id: dto.id,
                    dto: payload,
                });
            } finally {
                this.isTogglingActif = false;
            }
        },
        onEdit(dto: PreavisInterventionDTO) {
            if (!dto) return;
            this.editingId = dto.id as number;
            this.form = {
                objet: dto.objet || '',
                delai: dto.delai as any,
                actif: dto.actif ?? true,
                emails: (dto.emails || []).map(e => e.toLowerCase()),
            };
            this.showForm = true;
        },
        onDelete(dto: PreavisInterventionDTO) {
            if (!dto) return;
            this.alertPendingDelete = dto;
            this.showDeleteModal = true;
        },
        closeDeleteModal() {
            this.showDeleteModal = false;
            this.alertPendingDelete = null;
        },
        deleteDemande() {
            if (this.alertPendingDelete == null) {
                this.closeDeleteModal();
                return;
            }
            const id = this.alertPendingDelete.id;
            store.dispatch(`preavisInterventionState/${PreavisInterventionModuleAction.DELETE}`, id).finally(() => {
                this.closeDeleteModal();
            });
        },
        openCreate() {
            this.showCreateModal = true;
        },
        closeCreateModal() {
            if (this.createDirectPayload) {
                this.cancel();
            } else {
                this.showCreateModal = false;
            }
        },
        onCreateSuccess() {
            this.showCreateModal = false;
            this.showCreatedModal = true;
            store.commit(`preavisInterventionState/${PREAVIS.SET_CREATE_DIRECT}`, null);
            this.triggerSearch();
        },
        closeAllPreavisModals() {
            this.showCreatedModal = false;
            store.commit(`preavisInterventionState/${PREAVIS.SET_CREATE_DIRECT}`, null);
            store.commit(`preavisInterventionState/${PREAVIS.SET_MODAL}`, false);
        },
        onMesPreavis() {
            this.showCreatedModal = false;
            if (this.createDirectPayload) {
                store.commit(`preavisInterventionState/${PREAVIS.SET_MODAL}`, true);

                store.commit(`preavisInterventionState/${PREAVIS.SET_CREATE_DIRECT}`, null);
            }
        },
        onNouveauPreavis() {
            this.showCreatedModal = false;
            this.showCreateModal = true;
        },
        onEditStateChange(payload: any) {
            this.editFormValid = !!payload?.isFormValid;
            this.editStatePayload = payload;
        },
        closeForm() {
            this.showForm = false;
            this.editFormValid = false;
            this.editStatePayload = null;
        },
        async onSubmit() {
            (this.$refs.editFormulaireRef as any)?.validate();
            if (!this.editFormValid || this.isSubmitting) return;
            const dto: UpdatePreavisInterventionDTO = {
                objet: this.editStatePayload.objet.trim(),
                delai: this.editStatePayload.delai,
                actif: this.form.actif,
                emails: this.editStatePayload.emails,
            } as any;
            try {
                await store.dispatch(`preavisInterventionState/${PreavisInterventionModuleAction.UPDATE}`, {
                    id: this.editingId,
                    dto,
                });

                const error = store.getters['preavisInterventionState/error'];
                if (!error) {
                    this.closeForm();
                }
            } catch (e) {
                console.error('Erreur lors de la mise à jour :', e);
            }
        },
    },
});
</script>

<style scoped></style>
